// Scenario 2: Booked - Area Identified
// Shows lodging insights, area card, and map (lodging name identified but no exact CMS match)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { islandScopedDocumentFilter, sanityFetch } from '../../lib/sanity';
import HomebaseActionPanel from './HomebaseActionPanel';
import RegionsMapLite from '../maps/RegionsMapLite';
import {
  getCanonicalAreaTitle,
  getHomebaseAreaSlug,
  getHomebaseIslandKey,
  isHomebaseItineraryReady,
  MULTI_ISLAND_PLANNER_COMING_SOON_PATH,
} from '../../utils/homebaseGeography';

const BookedAreaScenario = ({ data, interviewId, planningState = {} }) => {
  const navigate = useNavigate();
  const [areaData, setAreaData] = useState(null);

  // Extract data before early return
  const islandKey = getHomebaseIslandKey(data);
  const itineraryReady = isHomebaseItineraryReady(islandKey);
  const area = data?.area;
  const region = data?.region;
  const areaSlug = getHomebaseAreaSlug(area, islandKey, data?.areaSlug);
  const areaTitle = getCanonicalAreaTitle(area, islandKey);
  
  // Fetch real area data from Sanity
  useEffect(() => {
    const fetchAreaData = async () => {
      if (!area) return;
      
      try {
        const query = `*[_type == "area" && ${islandScopedDocumentFilter()} && slug.current == $slug][0]{
          _id,
          title,
          oneLiner,
          heroImage{asset->{url}, alt},
          slug,
          fit,
          tradeoffs,
          walkability,
          beachProfile,
          diningDensity,
          logisticsNotes,
          region->{
            _id,
            title,
            slug
          }
        }`;
        
        const result = await sanityFetch(query, { slug: areaSlug });
        
        if (result) {
          setAreaData(result);
        } else {
          // Fall back to mock data
          setAreaData({
            title: area,
            heroImage: { asset: { url: '/api/placeholder/400/200' }, alt: `${area} landscape` },
            oneLiner: `Beautiful ${area} offers stunning scenery and local charm in ${region}.`
          });
        }
      } catch (error) {
        console.error('[BookedAreaScenario] Error fetching area data:', error);
        // Fall back to mock data
        setAreaData({
          title: area,
          heroImage: { asset: { url: '/api/placeholder/400/200' }, alt: `${area} landscape` },
          oneLiner: `Beautiful ${area} offers stunning scenery and local charm in ${region}.`
        });
      }
    };

    fetchAreaData();
  }, [area, region, areaSlug]);

  // Early return check after hooks
  if (!data || !data.summary) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white/70">No homebase data available for this scenario.</p>
      </div>
    );
  }

  const { summary } = data;
  const { lodgingInsights, proximityAdvantages, locationStrategies } = summary;

  const handleGenerateItinerary = () => {
    if (!itineraryReady) {
      navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH);
      return;
    }
    // Navigate immediately to preview page to show factoid rotator
    navigate(`/itinerary/${interviewId}`);
  };

  const handleEditInterview = () => {
    navigate(`/interview/review/${interviewId}`);
  };


  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 pb-8">
        
        {/* Primary CTAs */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          <div className="w-full md:w-72">
            <HomebaseActionPanel
              planningState={planningState}
              onGenerate={handleGenerateItinerary}
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>

        {/* LLM Insights */}
        <div className="glass-card rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 text-white">
            Why {areaTitle} is Perfect for Your Trip
          </h2>
          
          <div className="mb-4">
            <p className="text-white/90 leading-relaxed">
              {lodgingInsights}
            </p>
          </div>

          {proximityAdvantages && proximityAdvantages.length > 0 && (
            <div className="mb-4">
              <h3 className="font-semibold text-white mb-2">Location Advantages:</h3>
              <ul className="space-y-2">
                {proximityAdvantages.map((advantage, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-400 mr-2">•</span>
                    <span className="text-white/90">{advantage}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {locationStrategies && locationStrategies.length > 0 && (
            <div>
              <h3 className="font-semibold text-white mb-2">Strategic Insights:</h3>
              <ul className="space-y-2">
                {locationStrategies.map((strategy, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-400 mr-2">•</span>
                    <span className="text-white/90">{strategy}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Area Profile Content - Same structure as AreaDetail */}
        {areaData ? (
          <>
            {/* Hero Section */}
            <div className="relative h-[400px] overflow-hidden mb-12 rounded-lg">
              {areaData.heroImage?.asset?.url ? (
                <img
                  src={areaData.heroImage.asset.url}
                  alt={areaData.heroImage.alt || areaData.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                  <h1 className="text-5xl font-bold text-white">
                    {areaData.title}
                  </h1>
                </div>
              )}
              <div className="absolute inset-0 bg-black/40" />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="max-w-6xl mx-auto">
                  <h1 className="text-5xl font-bold mb-4">{areaData.title}</h1>
                  <p className="text-xl max-w-3xl">{areaData.oneLiner}</p>
                </div>
              </div>
            </div>

            {/* Overview Section */}
            <div className="grid md:grid-cols-2 gap-12 mb-12">
              <div>
                {areaData.fit && areaData.fit.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold mb-1 text-white">Good Fit For</h3>
                    <ul className="list-disc list-inside space-y-0">
                      {areaData.fit.map((item, index) => (
                        <li key={index} className="text-white/90">{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {areaData.tradeoffs && areaData.tradeoffs.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold mb-1 text-white">Trade-offs</h3>
                    <ul className="list-disc list-inside space-y-0">
                      {areaData.tradeoffs.map((tradeoff, index) => (
                        <li key={index} className="text-white/90">{tradeoff}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Inline Stats Section */}
                <div className="mb-4 space-y-1">
                  {areaData.walkability && (
                    <div className="flex items-center">
                      <span className="font-semibold mr-2 text-white">Walkability:</span>
                      <span className="text-white/90">
                        {areaData.walkability === 'high' ? 'High - Easy to walk everywhere' :
                         areaData.walkability === 'medium' ? 'Medium - Some walking possible' :
                         'Low - Car recommended'}
                      </span>
                    </div>
                  )}

                  {areaData.beachProfile && (
                    <div className="flex items-center">
                      <span className="font-semibold mr-2 text-white">Beach Profile:</span>
                      <span className="text-white/90">
                        {areaData.beachProfile === 'protected' ? 'Protected - Calm, family-friendly waters' :
                         areaData.beachProfile === 'snorkel' ? 'Snorkeling - Good visibility, marine life' :
                         areaData.beachProfile === 'surf' ? 'Surf - Waves for experienced swimmers' :
                         areaData.beachProfile === 'exposed' ? 'Exposed - Rough, scenic coastline' :
                         areaData.beachProfile}
                      </span>
                    </div>
                  )}

                  {areaData.diningDensity && (
                    <div className="flex items-center">
                      <span className="font-semibold mr-2 text-white">Dining Scene:</span>
                      <span className="text-white/90">
                        {areaData.diningDensity === 'high' ? 'High - Many dining options' :
                         areaData.diningDensity === 'medium' ? 'Medium - Several good restaurants' :
                         'Low - Limited dining options'}
                      </span>
                    </div>
                  )}
                </div>

                {areaData.logisticsNotes && (
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-white">Logistics & Getting Around</h3>
                    <p className="text-white/90">{areaData.logisticsNotes}</p>
                  </div>
                )}
              </div>

              {/* Interactive Map */}
              <div>
                <div className="h-[400px] rounded-lg overflow-hidden border border-gray-600">
                  <RegionsMapLite
                    islandKey={islandKey}
                    selectable={false}
                    selectedRegion={areaData?.region?.title}
                    selectedArea={areaData?.title}
                    focusedAreaMode={true}
                    className="w-full h-full"
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="mb-12 flex items-center justify-center h-48">
            <p className="text-white/70">Loading area profile...</p>
          </div>
        )}

        {/* Bottom CTAs */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          <div className="w-full md:w-72">
            <HomebaseActionPanel
              planningState={planningState}
              onGenerate={handleGenerateItinerary}
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookedAreaScenario;
